import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Bug,
  Database,
  Download,
  RefreshCw,
  Smartphone,
  Terminal,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Novel } from '../data/samples';
import { debugLogger } from '../utils/debugLogger';
import type { LogEntry } from '../utils/debugLogger';
import { getCustomNovels, saveCustomNovels } from '../utils/indexedDB';
import { NarouImporter } from '../utils/narouImporter';

export function DebugOverlay() {
  // 開発環境のみ表示
  if (!import.meta.env.DEV) return null;

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'logs' | 'db' | 'import' | 'system'>('logs');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [dbNovels, setDbNovels] = useState<Novel[]>([]);
  const [ncode, setNcode] = useState('n9669bk'); // デフォルトはなろうのテストコード
  const [importStatus, setImportStatus] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => {
    // ログの監視を開始
    const unsubscribe = debugLogger.subscribe((newLogs) => {
      setLogs(newLogs);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === 'db') {
      refreshDbList();
    }
  }, [isOpen, activeTab]);

  const refreshDbList = async () => {
    const list = await getCustomNovels();
    setDbNovels(list);
  };

  const handleClearDb = async () => {
    if (window.confirm('IndexedDB内のカスタム小説をすべて削除しますか？')) {
      await saveCustomNovels([]);
      debugLogger.warn('IndexedDB cleared via Debug Panel');
      refreshDbList();
    }
  };

  const handleDeleteNovel = async (id: string) => {
    const list = await getCustomNovels();
    const filtered = list.filter((n) => n.id !== id);
    await saveCustomNovels(filtered);
    debugLogger.info(`Deleted novel: ${id} from DB`);
    refreshDbList();
  };

  const handleImportNovel = async () => {
    if (!ncode.trim()) return;
    setIsImporting(true);
    setImportStatus('取得中...');
    debugLogger.info(`Starting debug import for N-code: ${ncode}`);
    try {
      const toc = await NarouImporter.fetchToc(ncode);

      // 最初の1エピソードをサンプルとしてインポート
      setImportStatus('エピソード1の本文を取得中...');
      const firstEp = await NarouImporter.fetchEpisode(ncode, 1);

      const newNovel: Novel = {
        id: `narou_${ncode}`,
        title: toc.title,
        author: toc.author,
        description: toc.description,
        coverColor: 'from-blue-600 to-indigo-800',
        content: `# ${toc.title}\n${toc.author}\n\n## 第1話: ${firstEp.title}\n\n${firstEp.content}`,
        scraped: true,
        ncode: ncode,
        url: `https://ncode.syosetu.com/${ncode}/`,
        episodesCount: toc.episodes.length,
        status: toc.status,
        genre: toc.genre,
        downloadedAt: new Date().toISOString(),
      };

      const list = await getCustomNovels();
      // 重複排除
      const filtered = list.filter((n) => n.id !== newNovel.id);
      await saveCustomNovels([...filtered, newNovel]);

      setImportStatus('インポート成功！書庫を確認してください。');
      debugLogger.info(`Debug import succeeded for ${toc.title} (${ncode})`);
      refreshDbList();
    } catch (e: unknown) {
      const errMessage = e instanceof Error ? e.message : String(e);
      setImportStatus(`エラー: ${errMessage}`);
      debugLogger.error(`Debug import failed for ${ncode}`, e);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      {/* フローティング起動ボタン */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 z-50 bg-rose-655 text-white p-3 rounded-full shadow-lg hover:bg-rose-500 transition-colors flex items-center justify-center cursor-pointer"
        style={{ backgroundColor: '#e11d48' }}
        aria-label="Open Debug Console"
      >
        <Bug className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <div className="absolute inset-0 z-50 flex flex-col justify-end bg-black/60 backdrop-blur-xs">
            {/* バックドロップの閉じるエリア */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Close when overlay is clicked */}
            <div className="absolute inset-0 -z-10" onClick={() => setIsOpen(false)} />

            {/* パネル本体 */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="bg-slate-900 border-t border-slate-800 rounded-t-2xl max-h-[70%] w-full flex flex-col text-slate-200 select-none pb-safe"
            >
              {/* ヘッダー */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-rose-500" />
                  <span className="font-bold text-sm">Developer Console</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-slate-400 hover:text-white rounded-full hover:bg-slate-800 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* タブナビゲーション */}
              <div className="flex border-b border-slate-800 bg-slate-950/50 text-xs">
                <button
                  type="button"
                  onClick={() => setActiveTab('logs')}
                  className={`flex-1 py-2.5 text-center font-medium border-b-2 cursor-pointer ${
                    activeTab === 'logs'
                      ? 'text-rose-500 border-rose-500 bg-slate-900/40'
                      : 'text-slate-400 border-transparent hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Terminal className="w-3.5 h-3.5" />
                    <span>Logs</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('db')}
                  className={`flex-1 py-2.5 text-center font-medium border-b-2 cursor-pointer ${
                    activeTab === 'db'
                      ? 'text-rose-500 border-rose-500 bg-slate-900/40'
                      : 'text-slate-400 border-transparent hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Database className="w-3.5 h-3.5" />
                    <span>DB</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('import')}
                  className={`flex-1 py-2.5 text-center font-medium border-b-2 cursor-pointer ${
                    activeTab === 'import'
                      ? 'text-rose-500 border-rose-500 bg-slate-900/40'
                      : 'text-slate-400 border-transparent hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Import</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('system')}
                  className={`flex-1 py-2.5 text-center font-medium border-b-2 cursor-pointer ${
                    activeTab === 'system'
                      ? 'text-rose-500 border-rose-500 bg-slate-900/40'
                      : 'text-slate-400 border-transparent hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Smartphone className="w-3.5 h-3.5" />
                    <span>SysInfo</span>
                  </div>
                </button>
              </div>

              {/* コンテンツ */}
              <div className="flex-1 overflow-y-auto p-4 text-xs font-mono">
                {/* 1. ログタブ */}
                {activeTab === 'logs' && (
                  <div className="flex flex-col gap-2 h-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400">直近のデバッグログ ({logs.length}件)</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => debugLogger.downloadLogs()}
                          className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded cursor-pointer transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>.logダウンロード</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => debugLogger.clearLogs()}
                          className="px-2.5 py-1 bg-red-950/50 hover:bg-red-900/50 text-red-400 border border-red-900/60 rounded cursor-pointer transition-colors"
                        >
                          クリア
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[300px] border border-slate-800 rounded bg-slate-950 p-2">
                      {logs.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">ログはまだありません</div>
                      ) : (
                        logs.map((log) => {
                          const isExpanded = expandedLogId === log.id;
                          const badgeColor = {
                            info: 'text-sky-400 bg-sky-950/40 border-sky-900/50',
                            warn: 'text-amber-400 bg-amber-950/40 border-amber-900/50',
                            error: 'text-red-400 bg-red-950/40 border-red-900/50',
                            validation: 'text-purple-400 bg-purple-950/40 border-purple-900/50',
                          }[log.type];

                          return (
                            <div
                              key={log.id}
                              className="border-b border-slate-900/80 pb-1.5 last:border-0"
                            >
                              {/* biome-ignore lint/a11y/useKeyWithClickEvents: Toggle logs expansion */}
                              <div
                                onClick={() => {
                                  if (log.details) {
                                    setExpandedLogId(isExpanded ? null : log.id);
                                  }
                                }}
                                className={`flex items-start gap-2 py-0.5 px-1 rounded transition-colors ${log.details ? 'cursor-pointer hover:bg-slate-900' : ''}`}
                              >
                                <span className="text-[10px] text-slate-500 whitespace-nowrap mt-0.5">
                                  {log.timestamp}
                                </span>
                                <span
                                  className={`text-[9px] px-1 border rounded uppercase font-bold tracking-wider ${badgeColor}`}
                                >
                                  {log.type}
                                </span>
                                <span className="flex-1 break-all text-slate-300">
                                  {log.message}
                                </span>
                                {!!log.details && (
                                  <span className="text-[10px] text-rose-400 shrink-0">
                                    {isExpanded ? '▼ 閉じる' : '▶ 詳細'}
                                  </span>
                                )}
                              </div>
                              {!!log.details && isExpanded && (
                                <pre className="mt-1.5 p-2 bg-slate-900 border border-slate-800 rounded text-[10px] text-slate-300 overflow-x-auto whitespace-pre-wrap max-h-[150px]">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* 2. データベース管理タブ */}
                {activeTab === 'db' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">IndexedDBカスタム小説リスト</span>
                      <button
                        type="button"
                        onClick={handleClearDb}
                        className="flex items-center gap-1 px-2.5 py-1 bg-red-950/60 hover:bg-red-900/60 text-red-400 border border-red-900/60 rounded cursor-pointer transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>DB全クリア</span>
                      </button>
                    </div>

                    <div className="border border-slate-800 rounded bg-slate-950 p-2 max-h-[250px] overflow-y-auto space-y-1.5">
                      {dbNovels.length === 0 ? (
                        <div className="text-center py-6 text-slate-500">
                          カスタム小説は登録されていません
                        </div>
                      ) : (
                        dbNovels.map((novel) => (
                          <div
                            key={novel.id}
                            className="flex items-center justify-between py-1.5 px-2 bg-slate-900/60 border border-slate-800 rounded"
                          >
                            <div className="min-w-0 pr-2">
                              <div className="font-bold truncate text-slate-300 text-[11px]">
                                {novel.title}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate">
                                ID: {novel.id} | 作者: {novel.author}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteNovel(novel.id)}
                              className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-red-400 cursor-pointer"
                              title="削除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* 3. クイックインポート */}
                {activeTab === 'import' && (
                  <div className="space-y-3">
                    <div className="text-slate-400 mb-1">なろう小説のデバッグ取得 (Zodテスト)</div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={ncode}
                        onChange={(e) => setNcode(e.target.value)}
                        placeholder="Nコード (例: n9669bk)"
                        className="flex-1 bg-slate-950 border border-slate-800 rounded px-2.5 py-1.5 text-slate-200 focus:outline-hidden focus:border-rose-500"
                        disabled={isImporting}
                      />
                      <button
                        type="button"
                        onClick={handleImportNovel}
                        className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-500 disabled:bg-rose-800 text-white rounded cursor-pointer disabled:cursor-not-allowed transition-colors font-bold"
                        style={{ backgroundColor: '#e11d48' }}
                        disabled={isImporting || !ncode.trim()}
                      >
                        <span>インポート</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {importStatus && (
                      <div
                        className={`p-2.5 border rounded text-[11px] ${
                          importStatus.includes('エラー')
                            ? 'bg-red-950/40 border-red-900/50 text-red-400'
                            : importStatus.includes('成功')
                              ? 'bg-emerald-950/40 border-emerald-900/50 text-emerald-400'
                              : 'bg-slate-850 border-slate-850 text-slate-400'
                        }`}
                      >
                        {importStatus}
                      </div>
                    )}

                    <div className="text-[10px] text-slate-500 leading-relaxed bg-slate-950/40 p-2 border border-slate-900 rounded">
                      <div className="font-bold mb-0.5 text-slate-400">📌 デバッグの流れ:</div>
                      1. なろう小説の Nコード を入力してインポートを実行します。
                      <br />
                      2. プロキシサーバー経由で小説目次データを取得し、Zodスキーマ検証を行います。
                      <br />
                      3. バリデーションエラーがあれば「Logs」タブに詳細なZodErrorが記録されます。
                    </div>
                  </div>
                )}

                {/* 4. システム情報 */}
                {activeTab === 'system' && (
                  <div className="space-y-3">
                    <div className="text-slate-400">環境・ステータス情報</div>
                    <div className="border border-slate-800 rounded bg-slate-950 p-2 space-y-1 text-slate-400">
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Environment:</span>
                        <span className="text-slate-300 font-bold">Development</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Safe Area Top:</span>
                        <span className="text-slate-300">var(--safe-area-top, 59px)</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Safe Area Bottom:</span>
                        <span className="text-slate-300">var(--safe-area-bottom, 34px)</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Screen Width:</span>
                        <span className="text-slate-300">{window.innerWidth}px</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Screen Height:</span>
                        <span className="text-slate-300">{window.innerHeight}px</span>
                      </div>
                      <div className="flex justify-between py-0.5 border-b border-slate-900">
                        <span>Network Status:</span>
                        <span
                          className={
                            navigator.onLine
                              ? 'text-emerald-400 font-bold'
                              : 'text-red-400 font-bold'
                          }
                        >
                          {navigator.onLine ? 'Online' : 'Offline'}
                        </span>
                      </div>
                      <div className="flex justify-between py-0.5">
                        <span>User Agent:</span>
                        <span
                          className="text-slate-500 text-[9px] truncate max-w-[200px]"
                          title={navigator.userAgent}
                        >
                          {navigator.userAgent}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
